/**
 * BUYKEYS.TSX - Compact Key Purchase Component
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import { getGameContract, getPAWContract } from '../utils/contract';
import { ethers } from 'ethers';
import { fetchPAWPrice, formatPAWToUSD } from '../utils/price';
import { FACTIONS, getFactionByContractId } from '../config/factions';
import { useDemoMode } from './DemoMode';
import { THALIAInline } from './THALIAIcon';
import { SocialSharing } from './SocialSharing';
import { BuyKeysWarnings } from './BuyKeysWarnings';

interface BuyKeysProps {
  account: string | null;
  currentPrice: bigint;
  onPurchase?: () => void;
  referrerFromUrl?: string | null;
  // Game state flags for contextual warnings
  fomoSurgeActive?: boolean;
  flashCrashActive?: boolean;
  isAssassinMode?: boolean;
  timeRemaining?: number;
}

const SMALL_KEY_AMOUNTS = [1, 2, 3, 4, 5];
const QUICK_KEY_AMOUNTS = [10, 50, 100, 500];

export default function BuyKeys({ 
  account, 
  currentPrice, 
  onPurchase, 
  referrerFromUrl,
  fomoSurgeActive = false,
  flashCrashActive = false,
  isAssassinMode = false,
  timeRemaining = -1,
}: BuyKeysProps) {
  // Demo mode
  const demoMode = useDemoMode();
  
  // State
  const [pawAmount, setPawAmount] = useState<string>('');
  const [selectedFaction, setSelectedFaction] = useState<number>(1); // Contract ID 1 = Raven
  const [referrer, setReferrer] = useState<string>('');
  const [purchaseMessage, setPurchaseMessage] = useState<string>('');
  const [showMessageInput, setShowMessageInput] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApproving, setIsApproving] = useState<boolean>(false);
  const [allowance, setAllowance] = useState<bigint>(0n);
  const [lockedFaction, setLockedFactionState] = useState<number | null>(() => {
    // Restore from sessionStorage to survive component remounts (tab switches)
    const stored = sessionStorage.getItem('nt3d_locked_faction');
    return stored ? Number(stored) : null;
  });
  const [cascadeLevel, setCascadeLevel] = useState<number>(0); // Was comboCount
  const [pawPrice, setPawPrice] = useState<number>(0);
  const [ethBalance, setEthBalance] = useState<bigint | null>(null);

  // Wrapper that also persists to sessionStorage
  const setLockedFaction = useCallback((factionId: number | null) => {
    setLockedFactionState(factionId);
    if (factionId !== null) {
      sessionStorage.setItem('nt3d_locked_faction', String(factionId));
    } else {
      sessionStorage.removeItem('nt3d_locked_faction');
    }
  }, []);
  const [lastPurchaseData, setLastPurchaseData] = useState<{ keys: string; amount: string } | null>(null);

  // Fetch PAW price
  useEffect(() => {
    const updatePrice = async () => {
      const price = await fetchPAWPrice();
      setPawPrice(price);
    };
    updatePrice();
    const interval = setInterval(updatePrice, 60000);
    return () => clearInterval(interval);
  }, []);

  // Use referrer from URL if provided
  useEffect(() => {
    if (referrerFromUrl && !referrer) {
      setReferrer(referrerFromUrl);
    }
  }, [referrerFromUrl, referrer]);

  // Fetch allowance and player data
  const fetchData = useCallback(async () => {
    if (!account) return;
    try {
      const [gameContract, pawContract] = await Promise.all([getGameContract(), getPAWContract()]);
      if (!pawContract || !gameContract) return;
      
      const gameAddress = await gameContract.getAddress();
      const currentAllowance = await pawContract.allowance(account, gameAddress);
      
      // Only update allowance if it increased, or if current state is 0
      // This prevents RPC lag from overwriting a known MaxUint256 approval
      setAllowance(prev => {
        if (prev === 0n || currentAllowance >= prev) {
          return currentAllowance;
        }
        // If RPC returns lower value but we had higher, keep higher (RPC lag)
        return prev;
      });

      // Get player's locked faction and cascade
      try {
        const roundData = await gameContract.currentRound();
        const roundId = roundData[0];
        const playerData = await gameContract.playerRoundData(roundId, account);
        const keysOwned = playerData[0];
        const factionId = Number(playerData[1]);
        const cascade = Number(playerData[4] || 0);
        
        if (keysOwned > 0n && factionId > 0) {
          setLockedFaction(factionId);
          setSelectedFaction(factionId);
        } else if (keysOwned === 0n) {
          // Only clear the lock if there's no recently-set persisted value
          // This prevents a race condition where fetchData runs after purchase
          // but the blockchain hasn't indexed the new keys yet
          const persisted = sessionStorage.getItem('nt3d_locked_faction');
          if (!persisted) {
            setLockedFaction(null);
          }
        }
        setCascadeLevel(cascade);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch player data:', e);
        // Don't clear lockedFaction on error — keep any persisted value
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  }, [account]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch ETH balance for gas warnings
  useEffect(() => {
    const fetchEthBalance = async () => {
      if (!account || !window.ethereum) {
        setEthBalance(null);
        return;
      }
      try {
        const provider = new ethers.BrowserProvider(window.ethereum);
        const balance = await provider.getBalance(account);
        setEthBalance(balance);
      } catch (e) {
        console.warn('[BuyKeys] Could not fetch ETH balance:', e);
      }
    };
    fetchEthBalance();
    const interval = setInterval(fetchEthBalance, 30000); // Update every 30s
    return () => clearInterval(interval);
  }, [account]);

  // Calculations
  const pawAmountBigInt = useMemo(() => {
    try {
      const parsed = parseFloat(pawAmount);
      if (isNaN(parsed) || parsed <= 0) return 0n;
      return ethers.parseEther(pawAmount);
    } catch { return 0n; }
  }, [pawAmount]);

  const estimatedKeys = useMemo(() => {
    if (currentPrice <= 0n || pawAmountBigInt <= 0n) return { post: 0n, bonus: false };
    const keys = pawAmountBigInt / currentPrice;
    const bonus = keys >= 100n;
    return { post: bonus ? keys + (keys / 20n) : keys, bonus };
  }, [pawAmountBigInt, currentPrice]);

  const needsApproval = allowance < pawAmountBigInt;

  // Parse transaction errors into user-friendly messages
  const parseTransactionError = (error: any): string => {
    const errorString = error?.reason || error?.message || String(error);
    const errorLower = errorString.toLowerCase();
    
    // Common revert reasons
    if (errorLower.includes('insufficient') && errorLower.includes('balance')) {
      return 'Insufficient PAW balance. Check your wallet balance.';
    }
    if (errorLower.includes('exceeds allowance') || errorLower.includes('not approved')) {
      return 'PAW not approved. Please approve PAW spending first.';
    }
    if (errorLower.includes('round ended') || errorLower.includes('round not active')) {
      return 'Round has ended. Wait for a new round to start.';
    }
    if (errorLower.includes('faction') && errorLower.includes('locked')) {
      return 'Wrong faction selected. You must buy with your locked faction.';
    }
    if (errorLower.includes('user rejected') || errorLower.includes('user denied')) {
      return 'Transaction cancelled by user.';
    }
    if (errorLower.includes('gas') || errorLower.includes('intrinsic gas too low')) {
      return 'Insufficient ETH for gas fees. Add more ETH to your wallet.';
    }
    if (errorLower.includes('nonce')) {
      return 'Transaction nonce error. Try resetting your wallet or wait a moment.';
    }
    if (errorLower.includes('timeout') || errorLower.includes('network')) {
      return 'Network error. Check your connection and try again.';
    }
    if (errorLower.includes('underpriced')) {
      return 'Gas price too low. Try increasing gas in your wallet.';
    }
    
    // Return original if no match
    return error?.reason || 'Transaction failed. Please try again.';
  };

  // Handlers
  const handleApprove = async () => {
    if (!account) return;
    setIsApproving(true);
    try {
      const pawContract = await getPAWContract(true);
      if (!pawContract) throw new Error('PAW contract not available');
      const gameContract = await getGameContract();
      if (!gameContract) throw new Error('Game contract not available');
      const gameAddress = await gameContract.getAddress();
      
      const tx = await pawContract.approve(gameAddress, ethers.MaxUint256);
      toast.loading('Approving PAW...', { id: 'approve' });
      await tx.wait();
      toast.success('PAW approved!', { id: 'approve' });
      
      // Immediately set allowance to max after successful approval
      // This avoids RPC sync issues where read provider hasn't caught up
      setAllowance(ethers.MaxUint256);
      
      // Also trigger background refresh in case we need fresh player data
      setTimeout(() => fetchData(), 2000);
    } catch (error: any) {
      console.error('Approve failed:', error);
      toast.error(parseTransactionError(error), { id: 'approve' });
    } finally {
      setIsApproving(false);
    }
  };

  const handleBuy = async () => {
    if (pawAmountBigInt <= 0n) return;
    
    // Demo mode: use simulated purchase
    if (demoMode.isDemo) {
      setIsLoading(true);
      try {
        const result = demoMode.buyKeys(pawAmountBigInt, selectedFaction);
        if (result.success) {
          toast.success(`🎮 Demo: Acquired ~${Number(result.keys)} keys!`, { id: 'buy' });
          setPawAmount('');
          setPurchaseMessage('');
          onPurchase?.();
        } else {
          toast.error(`🎮 Demo: ${result.message}`, { id: 'buy' });
        }
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Real mode: require account
    if (!account) return;
    setIsLoading(true);
    try {
      const gameContract = await getGameContract(true);
      if (!gameContract) throw new Error('Game contract not available');
      
      const refAddress = referrer && ethers.isAddress(referrer) ? referrer : ethers.ZeroAddress;
      
      toast.loading('Acquiring keys...', { id: 'buy' });
      const tx = await gameContract.buyKeys(pawAmountBigInt, selectedFaction, refAddress, purchaseMessage);
      await tx.wait();
      
      // Immediately lock faction after purchase
      setLockedFaction(selectedFaction);
      
      const keysAcquired = Number(estimatedKeys.post).toLocaleString();
      toast.success(`Acquired ~${keysAcquired} keys!`, { id: 'buy' });
      setLastPurchaseData({ keys: keysAcquired, amount: ethers.formatEther(pawAmountBigInt) });
      setPawAmount('');
      setPurchaseMessage('');
      onPurchase?.();
      // Delay fetchData to let blockchain state settle — immediate fetch can race
      // and return stale data (keysOwned=0) which would clear the faction lock
      setTimeout(() => fetchData(), 3000);
    } catch (error: any) {
      console.error('Buy failed:', error);
      toast.error(parseTransactionError(error), { id: 'buy' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.div
      className="relative glass-panel-3d"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="p-4 md:p-5 relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-neon-magenta/20 to-neon-purple/20 flex items-center justify-center border border-neon-magenta/30">
            <svg viewBox="0 0 24 24" fill="none" className="w-5 h-5 text-neon-magenta">
              <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M12 12l8 8M16 20l2-2M18 18l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="8" cy="8" r="2" fill="currentColor"/>
            </svg>
          </div>
          <div>
            <h2 className="font-orbitron text-lg text-neon-magenta">ACQUIRE KEYS</h2>
            <p className="text-xs text-gray-500 font-rajdhani">Delay the Singularity • Fight THALIA</p>
          </div>
        </div>
        
        {/* Input + Preview Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* PAW Input */}
          <div>
            <div className="relative">
              <input
                type="text"
                value={pawAmount}
                onChange={(e) => {
                  // Allow only numbers and dots (no commas in input - display only)
                  const raw = e.target.value.replace(/[^0-9.]/g, '');
                  setPawAmount(raw);
                }}
                className="w-full px-4 py-3 rounded-xl bg-void-black/50 border border-neon-magenta/30 text-white text-xl font-orbitron text-center focus:outline-none focus:border-neon-magenta focus:shadow-[0_0_20px_rgba(255,0,128,0.3)] transition-all"
                placeholder="PAW Amount"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-neon-magenta/70 font-mono font-bold">PAW</div>
            </div>
            {/* Formatted display */}
            {pawAmountBigInt > 0n && (
              <div className="text-center text-xs text-neon-magenta/70 mt-1 font-mono">
                {Number(ethers.formatEther(pawAmountBigInt)).toLocaleString(undefined, { maximumFractionDigits: 2 })} PAW
              </div>
            )}
            {/* USD Value Display */}
            {pawAmountBigInt > 0n && pawPrice > 0 && (
              <div className="text-center text-sm text-gray-400 mt-1 font-mono">
                ≈ ${formatPAWToUSD(pawAmountBigInt, pawPrice)} USD
              </div>
            )}
            {/* Quick key amounts - small */}
            <div className="flex gap-1.5 mt-2">
              {SMALL_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate PAW cost for desired key count using bonding curve
                      try {
                        const gameContract = await getGameContract();
                        if (!gameContract) return;
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                        }
                      }
                    }}
                    className="flex-1 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
            {/* Quick key amounts - large */}
            <div className="flex gap-1.5 mt-1.5">
              {QUICK_KEY_AMOUNTS.map((keyCount) => {
                return (
                  <motion.button
                    key={keyCount}
                    onClick={async () => {
                      // Calculate accurate PAW cost for desired key count using bonding curve
                      try {
                        const gameContract = await getGameContract();
                        if (!gameContract) return;
                        
                        // Get current round data for totalKeysSold
                        const roundData = await gameContract.currentRound();
                        const totalKeysSold = roundData.totalKeysSold || 0n;
                        
                        // Get base price and increment from contract
                        const basePrice = await gameContract.baseKeyPrice();
                        const increment = await gameContract.keyPriceIncrement();
                        
                        // Calculate total cost using bonding curve formula
                        // Cost = sum of (basePrice + (totalKeysSold + i) * increment) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * sum(totalKeysSold + i) for i = 0 to keyCount-1
                        // = keyCount * basePrice + increment * (keyCount * totalKeysSold + keyCount*(keyCount-1)/2)
                        const n = BigInt(keyCount);
                        const totalCost = n * basePrice + increment * (n * totalKeysSold + (n * (n - 1n)) / 2n);
                        
                        // Add 2% buffer for price movement during tx
                        const costWithBuffer = (totalCost * 102n) / 100n;
                        
                        setPawAmount(ethers.formatEther(costWithBuffer));
                      } catch (e) {
                        console.error('Failed to calculate key cost:', e);
                        // Fallback to simple calculation
                        if (currentPrice > 0n) {
                          const simpleCost = BigInt(keyCount) * currentPrice;
                          setPawAmount(ethers.formatEther(simpleCost));
                        }
                      }
                    }}
                    className="flex-1 py-1.5 rounded-lg border text-xs font-mono transition-all bg-void-black/50 border-gray-700 text-gray-400 hover:border-neon-cyan/50 hover:bg-neon-cyan/10 hover:text-neon-cyan"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {keyCount} <svg className="w-4 h-4 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                  </motion.button>
                );
              })}
            </div>
          </div>
          
          {/* Preview + Faction */}
          <div>
            <div className="flex items-center justify-between p-3 rounded-xl bg-void-black/50 border border-gray-700 mb-2">
              <span className="text-gray-400 text-sm font-rajdhani">You'll Get</span>
              <div className="text-right">
                <span className="font-orbitron text-lg text-neon-cyan">~{Number(estimatedKeys.post).toLocaleString()}</span>
                <span className="text-xs text-gray-500 ml-1">keys</span>
                {estimatedKeys.bonus && <span className="text-xs text-neon-cyan ml-2 animate-pulse">+5%!</span>}
              </div>
            </div>
            {/* Faction grid - 2 rows x 4 cols */}
            <div className="grid grid-cols-4 gap-2">
              {FACTIONS.map((faction) => {
                const isLocked = lockedFaction !== null && lockedFaction !== faction.contractId;
                const isSelected = selectedFaction === faction.contractId;
                return (
                  <motion.button
                    key={faction.id}
                    onClick={() => !isLocked && setSelectedFaction(faction.contractId)}
                    disabled={isLocked}
                    className={`flex flex-col items-center justify-center rounded-lg transition-all p-1.5 h-16 ${
                      isLocked ? 'opacity-30 cursor-not-allowed' : 
                      isSelected ? 'bg-neon-cyan/20 border-2 border-neon-cyan shadow-[0_0_15px_rgba(0,255,255,0.4)]' : 'bg-void-black/50 border border-transparent hover:border-gray-600'
                    }`}
                    whileHover={!isLocked ? { scale: 1.05 } : {}}
                    whileTap={!isLocked ? { scale: 0.95 } : {}}
                    title={`${faction.name} - ${faction.power}`}
                  >
                    <div className="relative">
                      <img 
                        src={faction.icon} 
                        alt={faction.name} 
                        className={`w-10 h-10 object-cover rounded-lg shadow-lg ${isLocked ? 'grayscale' : ''}`}
                        loading="lazy"
                        style={{ filter: isLocked ? 'brightness(0.4) grayscale(1)' : isSelected ? 'brightness(1.2) drop-shadow(0 0 8px rgba(0, 255, 255, 0.6))' : 'brightness(0.9)' }}
                      />
                      {isLocked && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-gray-500 text-lg">🔒</span>
                        </div>
                      )}
                      {isSelected && lockedFaction === faction.contractId && (
                        <div className="absolute -top-1 -right-1 bg-neon-cyan rounded-full w-4 h-4 flex items-center justify-center">
                          <span className="text-[8px]">✓</span>
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] mt-0.5 font-mono ${isLocked ? 'text-gray-600' : 'text-gray-400'}`}>
                      {isLocked ? '🔒' : faction.name}
                    </span>
                  </motion.button>
                );
              })}
            </div>
            {/* Faction power info */}
            {selectedFaction > 0 && (
              <div className="mt-2 p-2 rounded-lg bg-void-black/30 border border-green-700/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-neon-cyan font-mono">{getFactionByContractId(selectedFaction)?.power}</span>
                </div>
                <p className="text-[10px] text-green-400/70 mt-1 italic flex items-center gap-1">
                  <THALIAInline /> "{getFactionByContractId(selectedFaction)?.thaliaQuote}"
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* Referrer input */}
        <div className="mb-3">
          <input
            type="text"
            placeholder="Referrer address (optional)"
            value={referrer}
            onChange={(e) => setReferrer(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-gray-700 text-white text-sm font-mono placeholder:text-gray-600 focus:outline-none focus:border-neon-purple focus:shadow-[0_0_15px_rgba(153,50,204,0.2)] transition-all"
          />
        </div>

        {/* Purchase message toggle & input */}
        <div className="mb-4">
          <button
            onClick={() => setShowMessageInput(!showMessageInput)}
            className="text-xs text-gray-500 hover:text-neon-cyan transition-colors flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" fill="none" className="w-3 h-3">
              <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {showMessageInput ? 'Hide message' : 'Add message to purchase'}
          </button>
          {showMessageInput && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-2"
            >
              <input
                type="text"
                placeholder="Your message (shows in activity feed)"
                value={purchaseMessage}
                onChange={(e) => setPurchaseMessage(e.target.value.slice(0, 100))}
                maxLength={100}
                className="w-full px-3 py-2 rounded-lg bg-void-black/50 border border-neon-cyan/30 text-white text-sm font-rajdhani placeholder:text-gray-600 focus:outline-none focus:border-neon-cyan focus:shadow-[0_0_15px_rgba(0,255,255,0.2)] transition-all"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>💬 Message will be stored on-chain forever!</span>
                <span>{purchaseMessage.length}/100</span>
              </div>
            </motion.div>
          )}
        </div>
        
        {/* CASCADE indicator */}
        {cascadeLevel > 0 && (
          <div className="mb-3 p-2 rounded-lg bg-neon-purple/10 border border-neon-purple/30 flex items-center justify-between">
            <span className="text-sm text-neon-purple font-rajdhani flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" fill="currentColor"/></svg>
              CASCADE x{cascadeLevel}
            </span>
            <span className="text-xs text-gray-400">+{Math.min(cascadeLevel * 5, 15)}% variance</span>
          </div>
        )}
        
        {/* Low ETH warning - gas fees */}
        {account && ethBalance !== null && ethBalance < ethers.parseEther('0.001') && (
          <div className="mb-3 p-2 rounded-lg bg-amber-900/30 border border-amber-500/50 flex items-center gap-2">
            <span className="text-amber-400 text-base">⚠️</span>
            <div className="flex-1">
              <span className="text-xs text-amber-300 font-rajdhani">
                Low ETH balance ({parseFloat(ethers.formatEther(ethBalance)).toFixed(5)} ETH)
              </span>
              <p className="text-[10px] text-amber-400/70 mt-0.5">
                You need ETH for gas fees. Transactions may fail without sufficient ETH.
              </p>
            </div>
          </div>
        )}
        
        {/* Large purchase MEV warning */}
        {estimatedKeys.post > 1000n && (
          <div className="mb-3 p-2 rounded-lg bg-blue-900/30 border border-blue-500/50 flex items-center gap-2">
            <span className="text-blue-400 text-base">🤖</span>
            <div className="flex-1">
              <span className="text-xs text-blue-300 font-rajdhani">Large Purchase Detected</span>
              <p className="text-[10px] text-blue-400/70 mt-0.5">
                Large transactions may be targeted by MEV bots. Consider splitting into smaller purchases for better prices.
              </p>
            </div>
          </div>
        )}
        
        {/* Contextual game state warnings */}
        <BuyKeysWarnings
          isFOMOSurge={fomoSurgeActive}
          isFlashCrash={flashCrashActive}
          isAssassinMode={isAssassinMode}
          timeLeft={timeRemaining}
          extinctionEta={timeRemaining >= 0 && timeRemaining < 60 ? timeRemaining : undefined}
          isCursed={cascadeLevel > 0}
        />
        
        {/* Buy Button */}
        <motion.button
          onClick={needsApproval && !demoMode.isDemo ? handleApprove : handleBuy}
          disabled={isLoading || isApproving || (!account && !demoMode.isDemo)}
          className={`w-full py-4 rounded-xl font-orbitron text-lg font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
            !account && !demoMode.isDemo
              ? 'bg-gray-800 text-gray-500' 
              : needsApproval && !demoMode.isDemo
                ? 'bg-gradient-to-r from-neon-purple to-neon-magenta text-white' 
                : 'btn-buy'
          }`}
          whileHover={account || demoMode.isDemo ? { scale: 1.01 } : {}}
          whileTap={account || demoMode.isDemo ? { scale: 0.99 } : {}}
        >
          {demoMode.isDemo ? (isLoading ? 'Processing...' : '🎮 Buy Demo Keys') : (!account ? 'Connect Wallet' : isLoading ? 'Processing...' : isApproving ? 'Approving...' : needsApproval ? 'Approve PAW' : 'Execute Purchase')}
        </motion.button>
        
        {/* Post-purchase share prompt */}
        {lastPurchaseData && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-3 p-3 rounded-xl bg-neon-cyan/5 border border-neon-cyan/20 flex items-center justify-between gap-3"
          >
            <span className="text-xs text-gray-400 font-rajdhani">🔑 {lastPurchaseData.keys} keys acquired!</span>
            <SocialSharing 
              data={{ type: 'purchase', keys: lastPurchaseData.keys, amount: lastPurchaseData.amount }}
              playerAddress={account || undefined}
              className="flex-shrink-0"
            />
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

